import { useState } from 'react';
import { PRODUCT_PERMISSIONS } from '@/constants/permissions';
import { useQuery } from '@tanstack/react-query';
import { ConfirmDialog } from '@/components/common/ConfirmDialog';
import { DataTable } from '@/components/common/DataTable';
import { ErrorState } from '@/components/common/ErrorState';
import { useInfiniteTableQuery } from '@/hooks/useInfiniteTableQuery';
import { getActiveStatusFilterField } from '@/utils/filterFields';
import {
  bookEditionsQueryOptions,
  bookTypesQueryOptions,
  exportSpecimenList,
  specimensInfiniteQueryOptions,
} from '../api/product.api';
import { CatalogueListLayout } from '@/components/common/CatalogueListLayout';
import { getSpecimenColumns } from '../components/CatalogueColumns';
import { SpecimenFormModal } from '../components/SpecimenFormModal';
import { LOOKUP_PAGE_SIZE } from '../constants/product.constants';
import { useCatalogueFilters, type CatalogueRouteId } from '../hooks/useCatalogueFilters';
import { useDeleteSpecimen } from '../hooks/useCatalogueMutations';
import type { Specimen, SpecimenListParams } from '../types/product.types';
import { getBookFilterFields, getInsertedDateFilterFields } from '../utils/productFilterFields';
import { getSpecimenListStatCards } from '../utils/productStatCards';

const LOOKUP_PARAMS = {
  page: 1,
  pageSize: LOOKUP_PAGE_SIZE,
  isActive: true,
} as const;

interface SpecimenListPageProps {
  routeId: CatalogueRouteId;
  readOnly: boolean;
  shouldHideEarlySpecimens: boolean;
}

export function SpecimenListPage({
  routeId,
  readOnly,
  shouldHideEarlySpecimens,
}: SpecimenListPageProps) {
  const controller = useCatalogueFilters(routeId);
  const listParams: SpecimenListParams = {
    ...controller.search,
    hideEarlySpecimens: shouldHideEarlySpecimens,
  };
  const { rows, firstPageMeta, isLoading, isRefetching, loadError, refetch, loadMoreProps } =
    useInfiniteTableQuery(specimensInfiniteQueryOptions(listParams));

  const { data: bookTypes } = useQuery(bookTypesQueryOptions(LOOKUP_PARAMS));
  const { data: editions } = useQuery(bookEditionsQueryOptions(LOOKUP_PARAMS));

  const [editing, setEditing] = useState<Specimen | null>(null);
  const [isFormOpen, setFormOpen] = useState(false);
  const [pendingDelete, setPendingDelete] = useState<Specimen | null>(null);

  const remove = useDeleteSpecimen(() => setPendingDelete(null));

  const columns = getSpecimenColumns(
    controller.search.search,
    readOnly
      ? undefined
      : {
          onEdit: (row) => {
            setEditing(row);
            setFormOpen(true);
          },
          onDelete: setPendingDelete,
        },
  );

  const filterFields = [
    getActiveStatusFilterField(),
    ...getBookFilterFields({ bookTypes: bookTypes?.data, editions: editions?.data }),
    ...getInsertedDateFilterFields(),
  ];

  return (
    <CatalogueListLayout
      title={readOnly ? 'Specimen List View' : 'Specimen List'}
      dateRange={{ dateFrom: controller.search.insertedFrom, dateTo: controller.search.insertedTo }}
      search={{
        value: controller.searchText,
        onChange: controller.setSearchText,
        placeholder: 'Search by name or code',
      }}
      filters={controller}
      filterFields={filterFields}
      stats={{
        cards: getSpecimenListStatCards(firstPageMeta),
        isLoading,
        isFetching: isRefetching,
        isError: loadError !== null,
      }}
      addRequires={PRODUCT_PERMISSIONS.SPECIMEN_LIST_CREATE}
      exportRequires={PRODUCT_PERMISSIONS.SPECIMEN_LIST_EXPORT}
      onExport={() => exportSpecimenList(listParams)}
      {...(readOnly
        ? {}
        : {
            onAdd: () => {
              setEditing(null);
              setFormOpen(true);
            },
            addLabel: 'Add Specimen',
          })}
    >
      {loadError ? (
        <ErrorState
          error={loadError}
          fallbackTitle="Could not load the specimens"
          onRetry={() => void refetch()}
          fill
        />
      ) : (
        <DataTable
          data={rows}
          columns={columns}
          isLoading={isLoading}
          className="lg:flex-1"
          stickyColumnId="name"
          emptyMessage="No specimen matches"
          {...loadMoreProps}
        />
      )}

      {readOnly ? null : (
        <>
          <SpecimenFormModal specimen={editing} open={isFormOpen} onOpenChange={setFormOpen} />

          <ConfirmDialog
            open={pendingDelete !== null}
            onOpenChange={(open) => !open && setPendingDelete(null)}
            title="Remove this specimen?"
            description={
              pendingDelete === null
                ? ''
                : `"${pendingDelete.name}" will no longer be offered. A specimen that has already been handed out cannot be removed - make it inactive instead.`
            }
            confirmLabel="Remove"
            confirmVariant="destructive"
            isPending={remove.isPending}
            onConfirm={() => pendingDelete && remove.mutate(pendingDelete.id)}
          />
        </>
      )}
    </CatalogueListLayout>
  );
}
